"use client";
import { useTranslations } from "next-intl";
import type { ConnectionDetails } from "@client/index";
import { useState, useCallback, useEffect } from "react";
import { toastNotify } from "@client/components/ui/toast";
import { LocalUserChoices } from "@livekit/components-react";
import { GuestStorage, GuestMeetingEntry } from "@shared/utils";
import { Room, RoomEvent, RemoteParticipant } from "livekit-client";
import { guestJoin } from "@client/actions/guestUser/guestUser.actions";
import { useAppDispatch, useAppSelector } from "@client/store/store.hooks";
import { getMeetingDetails } from "@client/actions/meeting/meeting.actions";
import { setConnectionDetailsRedux } from "@client/store/slices/connection.slice";
import { MeetingExpiredState } from "@client/components/meet/MeetingExpiredScreen";
import {
  setIsJoining,
  setGuestData,
  selectGuestData,
  setJoinRejection,
  selectIsGuestMode
} from "@client/store/slices/preJoin.slice";
import { setIsAutoJoining } from "@client/store/slices/preJoin.slice";
import { STORAGE_KEYS } from "@shared/constants/constant";

const persistHostRoom = (roomName: string): void => {
  if (typeof window === "undefined" || !roomName) return;
  const key = STORAGE_KEYS.CLIENT.HOST_ROOMS;
  const hostRooms = JSON.parse(localStorage.getItem(key) || "[]") as string[];
  if (!hostRooms.includes(roomName)) {
    hostRooms.push(roomName);
    localStorage.setItem(key, JSON.stringify(hostRooms));
  }
};

// Helper: Build ConnectionDetails object from API response
const buildConnectionDetails = (data: Record<string, unknown>, isGuest = false): ConnectionDetails => {
  const details: ConnectionDetails = {
    serverUrl: String(data.serverUrl || ""),
    participantToken: String(data.participantToken || ""),
    roomName: String(data.roomName || ""),
    participantName: String(data.joinUserName || data.guestName || ""),
    joinUserName: String(data.joinUserName || data.guestName || ""),
    isHost: Boolean(data.isHost),
    canModerate: Boolean(data.canModerate ?? data.isModerator ?? data.isHost),
    isModerator: Boolean(data.isModerator ?? data.isHost),
    hostId: Number(data.hostId || 0),
    hostName: String(data.hostName || ""),
    roomId: String(data.roomId || data.meetingId || ""),
    userId: data.userId ? Number(data.userId) : undefined,
    isGuest,
    guestId: data.guestId ? String(data.guestId) : undefined,
    participantId: data.participantId ? String(data.participantId) : undefined,
    callStartedAt: data.callStartedAt ? String(data.callStartedAt) : null,
    chatId: data.chatId ? String(data.chatId) : null,
    chatEnabled: Boolean(data.chatEnabled),
    educatorGroupChannelId: data.educatorGroupChannelId ? String(data.educatorGroupChannelId) : null,
    odooEventId: data.odooEventId != null ? Number(data.odooEventId) : null,
    odooOccurrenceId: data.odooOccurrenceId != null ? Number(data.odooOccurrenceId) : null,
    odooRecurrenceId: data.odooRecurrenceId != null ? Number(data.odooRecurrenceId) : null,
    chatEmbedUrl: data.chatEmbedUrl ? String(data.chatEmbedUrl) : null,
    guestToken: data.guestToken ? String(data.guestToken) : null,
    avatarUrl: data.avatarUrl ? String(data.avatarUrl) : null,
    livekitRoomName: typeof data.livekitRoomName === "string" ? data.livekitRoomName : undefined,
    breakoutSessionId: typeof data.breakoutSessionId === "string" ? data.breakoutSessionId : undefined,
    breakoutRoomId: typeof data.breakoutRoomId === "string" ? data.breakoutRoomId : undefined,
    breakoutTitle: typeof data.breakoutTitle === "string" ? data.breakoutTitle : undefined,
    breakoutThemeColor: typeof data.breakoutThemeColor === "string" ? data.breakoutThemeColor : undefined
  };
  return details;
};

interface UseMeetingJoinProps {
  roomName: string;
}

export const useMeetingJoin = ({ roomName }: UseMeetingJoinProps) => {
  const dispatch = useAppDispatch();
  const t = useTranslations();
  const isGuestMode = useAppSelector(selectIsGuestMode);
  const guestData = useAppSelector(selectGuestData);

  const [connectionDetails, setConnectionDetails] = useState<ConnectionDetails>();
  const [lobbyRoom, setLobbyRoom] = useState<Room | null>(null);
  const [preJoinChoices, setPreJoinChoices] = useState<LocalUserChoices>();
  const [startPresenting, setStartPresenting] = useState(false);

  // Helper: Reset join state
  const resetJoinState = useCallback(() => {
    dispatch(setIsJoining(false));
    setPreJoinChoices(undefined);
    setLobbyRoom(null);
  }, [dispatch]);

  // Helper: Handle meeting expiration
  const handleMeetingExpired = useCallback(
    (_reason: MeetingExpiredState["reason"], _message: string, _title?: string | null) => {
      toastNotify.error(t("meeting.meetingEnded"));
      dispatch(setIsJoining(false));
    },
    [dispatch, t]
  );

  // Helper: Update connection
  const updateConnection = useCallback(
    (data: ConnectionDetails) => {
      setConnectionDetails(data);
      dispatch(setConnectionDetailsRedux(data));
    },
    [dispatch]
  );

  // Cleanup lobby on unmount
  useEffect(
    () => () => {
      lobbyRoom?.disconnect();
    },
    [lobbyRoom]
  );

  // 1. Waiting Room Flow
  const handleWaitingRoomFlow = useCallback(
    async (data: { serverUrl?: string; participantToken?: string; isGuest?: boolean; guestId?: string }) => {
      if (!data.serverUrl || !data.participantToken) {
        toastNotify.error(t("errors.missingDetails"));
        dispatch(setIsJoining(false));
        return;
      }

      const room = new Room({
        videoCaptureDefaults: { deviceId: "" },
        audioCaptureDefaults: { deviceId: "" },
        adaptiveStream: false,
        dynacast: false
      });

      try {
        await room.connect(data.serverUrl, data.participantToken, { autoSubscribe: false });

        const lobbyHandler = (raw: Uint8Array, _participant?: RemoteParticipant) => {
          let msg: Record<string, unknown>;
          try {
            msg = JSON.parse(new TextDecoder().decode(raw));
          } catch {
            return;
          }

          const cleanupAndDisconnect = () => {
            room.off(RoomEvent.DataReceived, lobbyHandler);
            room.disconnect();
          };

          if (msg.type === "join-approved") {
            cleanupAndDisconnect();
            GuestStorage.clearRejection(roomName);
            updateConnection(buildConnectionDetails(msg, Boolean(msg.isGuest)));
            dispatch(setIsJoining(false));
          } else if (msg.type === "join-rejected") {
            cleanupAndDisconnect();
            resetJoinState();
            const rejectionMessage = t("toast.info.rejected");
            dispatch(setJoinRejection({ isRejected: true, message: rejectionMessage }));
            GuestStorage.saveRejection(roomName, rejectionMessage);
          } else if (msg.type === "meeting-end") {
            cleanupAndDisconnect();
            resetJoinState();
            handleMeetingExpired("meeting-ended", String(msg.message || t("meeting.meetingEnded")));
          }
        };

        room.on(RoomEvent.DataReceived, lobbyHandler);
        setLobbyRoom(room);
      } catch {
        toastNotify.error(t("errors.failedToConnect"));
        dispatch(setIsJoining(false));
      }
    },
    [roomName, updateConnection, resetJoinState, handleMeetingExpired, dispatch, t]
  );

  // 2. Direct Join Flow
  const handleDirectJoinFlow = useCallback(
    (data: Record<string, unknown>, isGuest = false) => {
      if (!data.serverUrl || !data.participantToken || !data.roomName) {
        toastNotify.error(t("errors.missingRoomName"));
        dispatch(setIsJoining(false));
        return;
      }
      if (data.isHost) {
        persistHostRoom(String(data.roomName));
      }
      updateConnection(buildConnectionDetails(data, isGuest));
      dispatch(setIsJoining(false));
    },
    [updateConnection, dispatch, t]
  );

  // 3. Guest Join API
  const handleGuestJoin = useCallback(
    async (guestName: string, guestEmail: string) => {
      try {
        const result = await guestJoin({ roomName, guestName, guestEmail, guestId: guestData?.guestId });

        if (!result.status) {
          if (result.data?.meetingEnded) {
            handleMeetingExpired(
              result.data.reason as MeetingExpiredState["reason"],
              result.message || t("meeting.meetingEnded"),
              result.data.title
            );
          } else {
            const errorMessage = result.message || t("errors.joinFailed");
            const isRejection = errorMessage.toLowerCase().includes("rejected");
            if (isRejection) {
              dispatch(setJoinRejection({ isRejected: true, message: errorMessage }));
              GuestStorage.saveRejection(roomName, errorMessage);
            } else {
              toastNotify.error(errorMessage);
            }
            dispatch(setIsJoining(false));
          }
          return;
        }

        const data = result.data;
        if (!data) {
          toastNotify.error(t("errors.invalidResponse"));
          dispatch(setIsJoining(false));
          return;
        }

        const guestEntry: GuestMeetingEntry = {
          guestId: data.guestId || "",
          guestName: data.guestName || "",
          guestEmail: data.guestEmail || guestEmail,
          roomId: data.roomId || "",
          roomName: data.roomName || "",
          joinDate: data.joinDate || Date.now() / 1000
        };
        GuestStorage.saveData(guestEntry);
        dispatch(setGuestData(guestEntry));

        // Previously approved guest: Direct join
        if (data.waiting === false && data.previouslyApproved) {
          updateConnection(buildConnectionDetails({ ...data, isHost: false }, true));
          dispatch(setIsJoining(false));
          toastNotify.success(t("auth.welcomeBack"));
          return;
        }

        // New guest: Waiting room
        await handleWaitingRoomFlow({
          serverUrl: data.serverUrl,
          participantToken: data.participantToken,
          isGuest: true,
          guestId: data.guestId
        });
      } catch {
        toastNotify.error(t("errors.somethingWrong"));
        dispatch(setIsJoining(false));
      }
    },
    [roomName, guestData, handleWaitingRoomFlow, updateConnection, handleMeetingExpired, dispatch, t]
  );

  // 4. Main Submit Handler
  const handlePreJoinSubmit = useCallback(
    async (values: LocalUserChoices, shouldPresent = false, guestEmail = "") => {
      dispatch(setIsJoining(true));
      setPreJoinChoices(values);
      setStartPresenting(shouldPresent);

      if (isGuestMode) {
        await handleGuestJoin(values.username, guestEmail);
        return;
      }

      try {
        const meetingRes = await getMeetingDetails(roomName);

        if (!meetingRes.status) {
          if (meetingRes.data?.meetingEnded) {
            handleMeetingExpired(
              meetingRes.data.reason as MeetingExpiredState["reason"],
              meetingRes.message || t("meeting.meetingEnded"),
              meetingRes.data.title
            );
          } else {
            const errorMessage = meetingRes.message || t("errors.getDetailsFailed");
            const isRejection = errorMessage.toLowerCase().includes("rejected");
            if (isRejection) {
              dispatch(setJoinRejection({ isRejected: true, message: errorMessage }));
              if (isGuestMode) GuestStorage.saveRejection(roomName, errorMessage);
            } else {
              toastNotify.error(errorMessage);
            }
            dispatch(setIsJoining(false));
          }
          return;
        }

        const data = meetingRes.data;
        if (!data) {
          toastNotify.error(t("errors.noData"));
          dispatch(setIsJoining(false));
          return;
        }

        data.waiting === true ? await handleWaitingRoomFlow(data) : handleDirectJoinFlow(data);
      } catch {
        toastNotify.error(t("errors.somethingWrong"));
        dispatch(setIsJoining(false));
      }
    },
    [
      roomName,
      isGuestMode,
      handleGuestJoin,
      handleWaitingRoomFlow,
      handleDirectJoinFlow,
      handleMeetingExpired,
      dispatch,
      t
    ]
  );

  // 5. Auto-Join: skip pre-join screen when credentials are provided via query params
  const handleAutoJoin = useCallback(
    async (serverUrl: string, participantToken: string, waitingLobby = false) => {
      dispatch(setIsJoining(true));
      dispatch(setIsAutoJoining(true));
      try {
        if (waitingLobby) {
          await handleWaitingRoomFlow({ serverUrl, participantToken });
          return;
        }

        const meetingRes = await getMeetingDetails(roomName);
        const baseData: Record<string, unknown> =
          meetingRes.status && meetingRes.data
            ? { ...meetingRes.data, serverUrl, participantToken }
            : { serverUrl, participantToken, roomName, isHost: false, hostId: 0, hostName: "", roomId: roomName };

        if (baseData.waiting === true) {
          await handleWaitingRoomFlow({ serverUrl, participantToken });
          return;
        }

        if (baseData.isHost) {
          persistHostRoom(roomName);
        }
        handleDirectJoinFlow(baseData);
      } catch {
        toastNotify.error(t("errors.somethingWrong"));
      } finally {
        dispatch(setIsJoining(false));
        dispatch(setIsAutoJoining(false));
      }
    },
    [roomName, handleWaitingRoomFlow, handleDirectJoinFlow, dispatch, t]
  );

  return {
    connectionDetails,
    preJoinChoices,
    lobbyRoom,
    startPresenting,
    handlePreJoinSubmit,
    handleAutoJoin
  };
};
